import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { useEditManager } from "@/composables/edit-manager";
import { api, type types } from "@/lib/openapi";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";

export type ProcessElement = types.ProcessElement;
export type ProcessElementType = types.ProcessElementType;

// Different from the 'process_element', as it fetches a list of process_elements for you for the
// currently selected process.
export const useProcessElementsStore = defineStore("process_elements", () => {
    const isFetching = ref(false);
    const {
        process,
        stek,
        isFetching: isEntityBeingFetched,
    } = useCurrentEntityManager();
    const syncStore = useSyncStore();
    const { isEditing } = useEditManager();
    const processElements = ref<ProcessElement[] | undefined>(undefined);
    const archivedProcessElements = ref<ProcessElement[] | undefined>(
        undefined
    );

    const controller = ref(new AbortController());

    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities: SyncReturn) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) =>
                            identity.attribute === "process_definition"
                    )
                ) {
                    if (stek.value) {
                        refresh();
                    }
                }
            });
        }
    });

    async function refresh() {
        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!process.value) {
            processElements.value = [];
            archivedProcessElements.value = [];
            return;
        }

        isFetching.value = true;
        try {
            const { response, data = [] } =
                await api.startProcessProcessElementMgtGetProcessElementsByProcessStartPost(
                    {
                        body: {
                            process_id: process.value.identity,
                            include_archived: true,
                        },
                        signal: controller.value.signal,
                    }
                );
            if (response.ok) {
                processElements.value = data.filter(
                    (process_element) => !process_element.is_archived
                );
                archivedProcessElements.value = data.filter(
                    (process_element) => process_element.is_archived
                );
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
    }

    // Load process_elements when process has changed or definition is modidied.
    watch(
        [() => process.value?.identity, isEditing],
        ([identity, newIsEditing], [oldIdentity, wasEditing]) => {
            if (
                identity !== oldIdentity ||
                (newIsEditing === false && wasEditing === true)
            ) {
                refresh();
            }
        },
        { immediate: true }
    );

    return {
        processElements,
        archivedProcessElements,
        isFetching: computed(
            () => isFetching.value || isEntityBeingFetched.value
        ),
        refresh,
    };
});

export const useProcessElementsManager = () =>
    storeToRefs(useProcessElementsStore());
